import { Controller } from "@hotwired/stimulus";

const TOP_LEVEL_ACTIVE_CLASSES = [
  "bg-white/10",
  "text-white",
  "hover:bg-white/10",
];
const SUB_LINK_ACTIVE_CLASSES = ["bg-white/10", "text-white"];
const ACCORDION_SELECTOR = '[data-sidebar-target="accordion"]';
const ACCORDION_PANEL_SELECTOR = '[data-sidebar-target="accordion-panel"]';

export default class extends Controller {
  static targets = [
    "link",
    "indicator",
    "dot",
    "accordion",
    "accordionToggle",
    "accordionPanel",
    "chevron",
  ];
  static values = {
    currentPath: String,
    openAccordionIndex: Number,
  };

  connect() {
    this.handleTurboRender = this.handleTurboRender.bind(this);
    this.handleTurboLoad = this.handleTurboLoad.bind(this);
    this.handleTurboVisit = this.handleTurboVisit.bind(this);
    this.isNavigating = false;

    if (!this.hasCurrentPathValue) {
      this.currentPathValue = window.location.pathname;
    }

    document.addEventListener("turbo:render", this.handleTurboRender);
    document.addEventListener("turbo:load", this.handleTurboLoad);
    document.addEventListener("turbo:visit", this.handleTurboVisit);

    this.initializeActiveStates();
  }

  disconnect() {
    document.removeEventListener("turbo:render", this.handleTurboRender);
    document.removeEventListener("turbo:load", this.handleTurboLoad);
    document.removeEventListener("turbo:visit", this.handleTurboVisit);
  }

  handleTurboVisit() {
    this.isNavigating = true;
  }

  handleTurboRender() {
    if (this.isNavigating) {
      this.isNavigating = false;
      this.currentPathValue = window.location.pathname;
      this.updateActiveStates();
    }
  }

  handleTurboLoad() {
    if (!this.isNavigating) {
      this.currentPathValue = window.location.pathname;
      this.updateActiveStates();
    }
  }

  toggleAccordion(event) {
    const accordion = event.currentTarget.closest(ACCORDION_SELECTOR);
    if (!accordion) return;

    const index = parseInt(accordion.dataset.sidebarIndex);

    this.openAccordionIndexValue =
      this.openAccordionIndexValue === index ? null : index;
  }

  openAccordionIndexValueChanged() {
    this.updateAccordionStates();
  }

  updateAccordionStates() {
    this.accordionTargets.forEach((accordion) => {
      const accordionIndex = parseInt(accordion.dataset.sidebarIndex);
      const isOpen = accordionIndex === this.openAccordionIndexValue;

      const toggle = accordion.querySelector(
        '[data-sidebar-target="accordion-toggle"]',
      );
      if (toggle) {
        toggle.setAttribute("aria-expanded", String(isOpen));
      }

      const panel = accordion.querySelector(ACCORDION_PANEL_SELECTOR);
      if (panel) {
        panel.classList.toggle("hidden", !isOpen);
      }

      const chevron = accordion.querySelector(
        '[data-sidebar-target="chevron"]',
      );
      if (chevron) {
        chevron.classList.toggle("rotate-180", isOpen);
      }
    });
  }

  initializeActiveStates() {
    this.updateActiveStates();

    // Fresh controllers connect fully collapsed, so reopen the active team's accordion.
    for (const link of this.linkTargets) {
      const accordion = link.closest(ACCORDION_SELECTOR);
      if (this.activeFor(link) && accordion) {
        this.openAccordionIndexValue = parseInt(accordion.dataset.sidebarIndex);
        break;
      }
    }
  }

  updateActiveStates() {
    this.linkTargets.forEach((link) => {
      this.setLinkActive(link, this.activeFor(link));
    });

    this.updateAccordionStates();
  }

  // Sub-links live on a route prefix (`/spaces/:id/expenses/123` stays active);
  // top-level links match exactly, like `isActive` in Sidebar.tsx.
  activeFor(link) {
    const linkPath = link.dataset.sidebarPath || link.getAttribute("href");
    const prefixMatch = link.dataset.sidebarPrefixMatch === "true";

    return prefixMatch
      ? this.currentPathValue.startsWith(linkPath)
      : linkPath === this.currentPathValue;
  }

  setLinkActive(link, active) {
    const isSubLink = link.closest(ACCORDION_PANEL_SELECTOR);
    const activeClasses = isSubLink
      ? SUB_LINK_ACTIVE_CLASSES
      : TOP_LEVEL_ACTIVE_CLASSES;
    const marker = link.querySelector(
      `[data-sidebar-target="${isSubLink ? "dot" : "indicator"}"]`,
    );

    activeClasses.forEach((cls) => link.classList.toggle(cls, active));
    if (marker) {
      marker.classList.toggle(isSubLink ? "hidden" : "opacity-0", !active);
    }
  }
}
